import { units } from '@dynatrace-sdk/units';

import { getHeaderProps } from './get-header-props.js';
import { useGridPlotSize } from '../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { OverlayTooltip } from '../../../core/components/overlay-tooltip/OverlayTooltip.js';
import { defaultUnitFormatter } from '../../../core/utils/defaultUnitFormatter.js';
import { safeGet } from '../../../core/utils/safe-get.js';
import { useAxesFormatter } from '../../hooks/useAxesFormatter.js';
import { useVisibleAxesConfig } from '../../hooks/useVisibleAxesConfig.js';
import { useVisibleAxesScales } from '../../hooks/useVisibleAxesScales.js';
import { useSelectState } from '../../store/selectors.js';
import type {
  AxisIdToFormatter,
  XYChartAxisInternal,
} from '../../types/xy-chart-internals.js';

function getAxisFormatter(
  axesIdToFormatter: AxisIdToFormatter,
  axisConfig?: XYChartAxisInternal,
) {
  if (!axisConfig) {
    return undefined;
  }
  if (axisConfig.isRelative) {
    return defaultUnitFormatter(units.percentage.percent);
  }
  return safeGet(axesIdToFormatter, axisConfig.id);
}

export const TooltipHeader = (props: { subheader: string }) => {
  const selectState = useSelectState();
  const axesIdToFormatter = useAxesFormatter();
  const { top: topScale, bottom: bottomScale } = useVisibleAxesScales();
  const { top: topConfig, bottom: bottomConfig } = useVisibleAxesConfig();
  const { width } = useGridPlotSize();

  if (!selectState) {
    return null;
  }

  const topFormatter = getAxisFormatter(axesIdToFormatter, topConfig);
  const bottomFormatter = getAxisFormatter(axesIdToFormatter, bottomConfig);

  const start = selectState.selectionPercentage
    ? (width * selectState.selectionPercentage[0]) / 100
    : selectState.start.relative.x;
  const end = selectState.selectionPercentage
    ? (width * selectState.selectionPercentage[1]) / 100
    : selectState.end.relative.x;

  const topDuration = topConfig && topScale && !!topFormatter;
  const bottomDuration = bottomConfig && bottomScale && !!bottomFormatter;

  const header1Props = topDuration
    ? getHeaderProps({
        axisConfig: topConfig,
        scale: topScale,
        formatter: topFormatter,
        startPixels: start,
        endPixels: end,
        suffix: '1',
      })
    : {};
  const header2Props = bottomDuration
    ? getHeaderProps({
        axisConfig: bottomConfig,
        scale: bottomScale,
        formatter: bottomFormatter,
        startPixels: start,
        endPixels: end,
        suffix: '2',
      })
    : {};

  return (
    <OverlayTooltip.Header
      {...header1Props}
      {...header2Props}
      subheader={props.subheader}
    />
  );
};
